// The five summary cards of the Purchase page, shared by Purchase Approvals
// (AdminPurchaseApprovalsView) and the purchase manager's Pending Deliveries
// (PurchasePanel). Styled by AdminPurchaseApprovals.css (`apa-card`).
import React from 'react';
import { FileText, ClipboardCheck, Truck, House, IndianRupee } from 'lucide-react';
import './AdminPurchaseApprovals.css';

// Purchase requests counted as spent: delivered to the Balgruha (the last
// step of pending -> ordered -> delivered_store -> delivered_balagruha).
const SPENT_STATUSES = ['delivered_balagruha', 'completed'];

// The purchase request status each request card stands for.
export const CARD_STATUS = {
    pendingOrders: 'pending',
    pendingStoreDelivery: 'ordered',
    pendingBalagruhaDelivery: 'delivered_store',
};

export const CARD_TITLE = {
    pendingOrders: 'Pending Orders',
    pendingApproval: 'Pending Approval',
    pendingStoreDelivery: 'Pending Delivery to Yashodhan',
    pendingBalagruhaDelivery: 'Pending Balgruha Delivery',
    spent: 'Total Spent',
};

export const formatRupees =(value) => Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const statsOf = (purchaseData) => {
    const countOf = (status) => purchaseData.filter((request) => request.status === status).length;
    const spent = purchaseData
        .filter((request) => SPENT_STATUSES.includes(request.status))
        .reduce((sum, request) => sum + (Number(request.actualTotalCost) || Number(request.totalEstimatedCost) || 0), 0);
    return {
        pendingOrders: countOf(CARD_STATUS.pendingOrders),
        pendingStoreDelivery: countOf(CARD_STATUS.pendingStoreDelivery),
        pendingBalagruhaDelivery: countOf(CARD_STATUS.pendingBalagruhaDelivery),
        spent,
    };
};

// onSelect(cardKey) runs when a card is clicked (Total Spent is not
// clickable); activeKey marks the selected card; hiddenKeys leaves cards out.
export default function PurchaseSummaryCards({
    purchaseData = [],
    pendingApprovals = 0,
    approvalsHint = 'Need admin review',
    activeKey = null,
    onSelect = () => {},
    hiddenKeys = [],
}) {
    const stats = statsOf(purchaseData);
    const allCards = [
        {
            key: 'pendingOrders',
            value: stats.pendingOrders,
            title: CARD_TITLE.pendingOrders,
            hint: 'New requests to be processed',
            Icon: FileText,
            tone: 'blue',
        },
        {
            key: 'pendingApproval',
            value: pendingApprovals,
            title: CARD_TITLE.pendingApproval,
            hint: approvalsHint,
            Icon: ClipboardCheck,
            tone: 'orange',
        },
        {
            key: 'pendingStoreDelivery',
            value: stats.pendingStoreDelivery,
            title: CARD_TITLE.pendingStoreDelivery,
            hint: 'Approved, yet to send',
            Icon: Truck,
            tone: 'purple',
        },
        {
            key: 'pendingBalagruhaDelivery',
            value: stats.pendingBalagruhaDelivery,
            title: CARD_TITLE.pendingBalagruhaDelivery,
            hint: 'Sent, yet to deliver',
            Icon: House,
            tone: 'green',
        },
        {
            key: 'spent',
            value: `₹ ${formatRupees(stats.spent)}`,
            title: CARD_TITLE.spent,
            hint: 'For delivered items',
            Icon: IndianRupee,
            tone: 'red',
            static: true,
        },
    ];
    const cards = allCards.filter((card) => !hiddenKeys.includes(card.key));

    return (
        <div className="apa-cards">
            {cards.map((card) => {
                const active = card.key === activeKey;
                const content = (
                    <>
                        <span className={`apa-card-icon apa-tone-${card.tone}`}>
                            <card.Icon size={22} aria-hidden="true" />
                        </span>
                        <span className="apa-card-text">
                            <span className={`apa-card-value apa-value-${card.tone}`}>{card.value}</span>
                            <span className="apa-card-title">{card.title}</span>
                            <span className="apa-card-hint">{card.hint}</span>
                        </span>
                    </>
                );
                return card.static ? (
                    <div key={card.key} className={`apa-card apa-card-${card.tone}`}>
                        {content}
                    </div>
                ) : (
                    <button
                        type="button"
                        key={card.key}
                        className={`apa-card apa-card-${card.tone}${active ? ' active' : ''}`}
                        onClick={() => onSelect(card.key)}
                        aria-pressed={active || undefined}
                    >
                        {content}
                    </button>
                );
            })}
        </div>
    );
}
